import { IconButton } from '@mui/material';
import { closeSnackbar, OptionsObject, useSnackbar } from 'notistack';
import { createContext, ReactNode, useEffect } from 'react';
import { IconifyIconEnum } from 'src/@types/icon';
import { JobNotificationStatus, JobType } from 'src/@types/job';
import IconifyIcon from 'src/components/icons/IconifyIcon';
import useLocales from 'src/hooks/useLocales';
import { socket } from 'src/services/socket/socket.io';

enum mapJobStatusEnum {
  COMPLETED = 'completed',
  FAILED = 'failed',
}

enum mapJobTypeEnum {
  CREATE_GUEST_INVITATIONS = 'createGuestInvitations',
  GENERATE_COUPON_CODES = 'generateCouponCodes',
  GENERATE_GUEST_TICKETS = 'generateGuestTickets',
}

type JobStatusPayload = {
  jobType: Exclude<JobType, JobType.EXPORT>;
  jobStatus: JobNotificationStatus;
};

const JobStatusContext = createContext(null);

type JobStatusProviderProps = {
  children: ReactNode;
};

export function JobStatusProvider({ children }: JobStatusProviderProps) {
  const { translate } = useLocales();

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const handleJobStatus = ({ jobType, jobStatus }: JobStatusPayload) => {
      closeSnackbar();

      const snackbarProps: OptionsObject = {
        variant: jobStatus === JobNotificationStatus.COMPLETED ? 'success' : 'error',
        persist: true,
        action: (key) => (
          <IconButton onClick={() => closeSnackbar(key)}>
            <IconifyIcon icon={IconifyIconEnum.Close} />
          </IconButton>
        ),
      };

      enqueueSnackbar(
        translate(`jobNotifications.${mapJobTypeEnum[jobType]}.${mapJobStatusEnum[jobStatus]}`),
        snackbarProps
      );
    };

    socket.on('onJobStatus', handleJobStatus);

    return () => {
      socket.off('onJobStatus', handleJobStatus);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <JobStatusContext.Provider value={null}>{children}</JobStatusContext.Provider>;
}
